/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { EntityId } from '@smartsheet-dataflow/formjs-react';
import {
  Button,
  colors,
  sizes,
  Spacer,
  TypeRampV2,
} from '@smartsheet/lodestar-core';
import { AddIcon, ToolbarChevronUpIcon } from '@smartsheet/lodestar-icons';
import React, { useState } from 'react';
import useCollapse from 'react-collapsed';
import { randomString } from '../../utils/random';
import { FormJSInputGroup } from './InputGroup';

interface InputGroupArrayProps extends EntityId {
  label: string;
  entities: string[];
  arrayItemsLabel: string;
  addNewItem: (inputId: string) => void;
  removeItem: (inputId: string) => void;
  isOpen: boolean;
}

export const InputGroupArray = ({
  id,
  label,
  entities,
  arrayItemsLabel,
  addNewItem,
  removeItem,
  isOpen = false,
}: InputGroupArrayProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(isOpen);
  const { getToggleProps, getCollapseProps } = useCollapse({
    isExpanded,
  });

  const style = css`
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    :hover,
    :focus {
      div {
        background-color: ${colors.focusLight20};
      }
    }
  `;

  const labelStyle = css`
    line-height: ${sizes.xLarge}px !important;
    width: calc(100% - ${sizes.xLarge}px);
  `;

  const chevronContainerStyle = css`
    border-radius: 2px;
    box-sizing: border-box;
    height: ${sizes.xLarge}px;
    width: ${sizes.xLarge}px;
    padding: calc(11px / 2);
  `;

  const chevronStyle = css`
    transition: transform 200ms;
    ${isExpanded ? 'transform: rotate(180deg)' : 'transform: rotate(90deg)'}
  `;

  return (
    <React.Fragment>
      <div
        css={style}
        {...getToggleProps({
          onClick: () => {
            setIsExpanded(!isExpanded);
          },
        })}
        id={id}
      >
        <TypeRampV2 system="lodestar" variant={'headerSmall'} css={labelStyle}>
          {label}
        </TypeRampV2>
        <div css={chevronContainerStyle}>
          <ToolbarChevronUpIcon css={chevronStyle} />
        </div>
      </div>
      <Spacer orientation="column" space="xLarge" {...getCollapseProps()}>
        {Array.isArray(entities) &&
          entities.map((childId: string, index: number) => (
            <FormJSInputGroup
              key={childId}
              id={childId}
              label={`${arrayItemsLabel || label} ${index + 1}`}
              onRemove={() => removeItem(childId)}
            />
          ))}
        <Button
          iconAfter={<AddIcon />}
          onClick={() => addNewItem(`item${randomString()}`)}
        >
          Add to {label}
        </Button>
      </Spacer>
    </React.Fragment>
  );
};
